import React, { useEffect, useState } from 'react';
import { Plus, RefreshCw, Search, Settings } from 'lucide-react';
import {
  DetailModal,
  MobileCard,
  STATUS_OPTIONS,
  Summary,
} from './ContractRenewalParts';
import ContractRenewalCalendar from './ContractRenewalCalendar';
import ContractRenewalSettingsModals from './ContractRenewalSettingsModals';
import ContractRenewalTable from './ContractRenewalTable';
import CompanyFilterChips from '../integrated/CompanyFilterChips';
import { normalizeCompanyCode, useContractRenewal } from './useContractRenewal';
import {
  inputStyle,
  pageStyle,
  summaryGridStyle,
} from './styles';
import {
  stdBlackBtn,
  stdHeaderRow,
  stdOutlineBtn,
  stdPageSubtitle,
  stdPageTitle,
  stdSearchIcon,
  stdSearchInput,
  stdSearchWrap,
  stdTabBtn,
  stdTabGroup,
} from '../adminlayout/designStandard';

// 표준 버튼에 lucide 아이콘을 넣을 때 정렬용 (스타일만, 토큰 색·모양 유지)
const iconBtnAlign = { display: 'inline-flex', alignItems: 'center', gap: 6 };

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

export default function ContractRenewalManagement({
  companyCode = 'PINE',
  pageTitle = '계약갱신관리',
  apiBase,
  integratedMenuId = null,
  companyCodes = [],
  selectedCompanyCodes,
  setSelectedCompanyCodes,
}) {
  const isMobile = useIsMobile();
  // 상태·불러오기·저장은 useContractRenewal.js (2026-11-04 나눔, 동작 변화 없음)
  const {
    normalizedCompanyCodes, activeCompanyCodes, resolvedApiBase, canManageSettings, rows, summary,
    status, setStatus, search, setSearch, includeResigned, setIncludeResigned,
    loading, savingId, message, setMessage, detail, setDetail,
    settingsOpen, setSettingsOpen, manualOpen, setManualOpen, setupCompanyCode, setSetupCompanyCode,
    memoDrafts, setMemoDrafts, loadRows, openDetail, saveMemo, undoLast,
    runAction, handlers, setupCompanyOptions, settingsCompanyCode, calendarCompanyCode, openManualSettings,
    openDepartmentSettings,
  } = useContractRenewal({ companyCode, apiBase, integratedMenuId, companyCodes, selectedCompanyCodes });

  const companySelector = integratedMenuId && setupCompanyOptions.length > 1 && (
    <label style={{ display: 'grid', gap: 5, marginBottom: 12, color: 'var(--text-sub)', fontSize: 12, fontWeight: 850 }}>
      <span>적용 회사</span>
      <select
        style={{ ...inputStyle, maxWidth: 180 }}
        value={setupCompanyCode}
        onChange={(event) => setSetupCompanyCode(normalizeCompanyCode(event.target.value))}
      >
        {setupCompanyOptions.map((code) => (
          <option key={code} value={code}>{code}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div style={pageStyle}>
      <div style={stdHeaderRow(isMobile)}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={stdPageTitle}>{pageTitle}</h2>
            {integratedMenuId && normalizedCompanyCodes.length > 0 && setSelectedCompanyCodes && (
              <CompanyFilterChips
                companyCodes={normalizedCompanyCodes}
                selected={activeCompanyCodes}
                onChange={setSelectedCompanyCodes}
              />
            )}
          </div>
          <div style={stdPageSubtitle}>직원 계약 갱신 일정을 관리합니다.</div>
        </div>
        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {canManageSettings && <button type="button" style={{ ...stdOutlineBtn, ...iconBtnAlign }} onClick={openDepartmentSettings}>
            <Settings size={15} /> 자동등록 설정
          </button>}
          <button
            type="button"
            style={{ ...stdOutlineBtn, ...iconBtnAlign, opacity: loading ? 0.65 : 1, cursor: loading ? 'wait' : 'pointer' }}
            onClick={loadRows}
            disabled={loading}
          >
            <RefreshCw size={15} /> 새로고침
          </button>
          {canManageSettings && <button type="button" style={{ ...stdBlackBtn, ...iconBtnAlign }} onClick={openManualSettings}>
            <Plus size={15} /> 직접 등록
          </button>}
        </div>
      </div>

      <ContractRenewalCalendar
        rows={rows}
        onSelectRow={openDetail}
        isMobile={isMobile}
        apiBase={resolvedApiBase}
        companyCode={calendarCompanyCode}
      />

      <div style={summaryGridStyle}>
        <Summary label="전체" value={summary.total || 0} />
        <Summary label="수습검토" value={summary.probation || 0} tone="#dc2626" />
        <Summary label="계약임박" value={summary.contract_due || 0} tone="#b45309" />
        <Summary label="여유" value={summary.ok || 0} tone="#15803d" />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ ...stdSearchWrap(isMobile), marginBottom: 0, flex: isMobile ? '1 1 100%' : '0 1 400px' }}>
          <Search size={15} style={stdSearchIcon} />
          <input style={stdSearchInput} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="이름 또는 아이디" />
        </div>
        <select style={{ ...inputStyle, height: 38, borderRadius: 12 }} value={status} onChange={(event) => setStatus(event.target.value)}>
          {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <div style={stdTabGroup}>
          <button type="button" style={stdTabBtn(!includeResigned)} onClick={() => setIncludeResigned(false)}>
            재직중
          </button>
          <button type="button" style={stdTabBtn(includeResigned)} onClick={() => setIncludeResigned(true)}>
            전체(퇴사 포함)
          </button>
        </div>
      </div>

      {message && <div style={{ color: 'var(--text)', fontWeight: 800 }}>{message}</div>}

      {isMobile ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((row) => <MobileCard key={row.user_id} row={row} saving={savingId === row.user_id} handlers={handlers} />)}
        </div>
      ) : (
        <ContractRenewalTable rows={rows} savingId={savingId} handlers={handlers} />
      )}

      <DetailModal
        detail={detail}
        memoDrafts={memoDrafts}
        saving={Boolean(savingId)}
        onClose={() => setDetail(null)}
        onMemo={(termId, value) => setMemoDrafts((prev) => ({ ...prev, [termId]: value }))}
        onSaveMemo={saveMemo}
        onRenew={(row) => runAction(row, 'renew')}
        onEnd={(row) => runAction(row, 'end')}
        onUndo={undoLast}
      />

      {canManageSettings && (
        <ContractRenewalSettingsModals
          manualOpen={manualOpen}
          settingsOpen={settingsOpen}
          apiBase={resolvedApiBase}
          companyCode={settingsCompanyCode}
          companySelector={companySelector}
          onCloseManual={() => setManualOpen(false)}
          onCloseSettings={() => setSettingsOpen(false)}
          onChanged={loadRows}
          setMessage={setMessage}
        />
      )}
    </div>
  );
}
